import { getMappedPipingOptions } from 'packages/common/resources/piping-constants';

import { createThunkRequest, POST } from '@webapp/account/src/libs/request';
import { mapPage } from '@webapp/account/src/resources/common';
import * as LayoutRes from '@webapp/account/src/resources/layout';
import { fillPresetColors, onAddQuestionOrRowFillers } from '@webapp/account/src/resources/questions/fillers';
import { selectSettingsState } from '@webapp/account/src/resources/settings/selectors';

import type { QuestionCol } from '@webapp/common/entities/question-col';
import type { QuestionRow } from '@webapp/common/entities/question-row';
import { deepMapQuestion, mapCategory, mapCol, mapNested, mapRow } from '@webapp/common/lib/map';
import { getParamsData } from '@webapp/common/lib/params';
import type { IQuestionCategory, Question } from '@webapp/common/resources/entities/question';
import { reorderQuestions } from '@webapp/common/resources/question-types';
import type { AnswerType, QuestionType } from '@webapp/common/resources/survey';

import { selectCurrentSurveyId } from '../survey/selectors';

import { filterOnlyNewRows } from './lib';
import { mapQuestion, reverseMapCol, reverseMapQuestion, reverseMapRow } from './map';
import { MultiCreateFillers } from './multi-create-fillers';
import { QuestionValuesKeys } from './questions';
import { selectNearByIds, selectQuestionById, selectQuestions } from './selectors';

export const addQuestionRows = createThunkRequest(
    'questions/addRows',
    async (
        {
            questionId,
            rows,
            rowsToDelete = [],
        }: {
            questionId: number;
            rows: Array<QuestionRow>;
            rowsToDelete: Array<number>;
        },
        { dispatch, getState },
    ) => {
        if (rowsToDelete.length > 0) {
            await dispatch(removeQuestionRow({ questionId, rowId: rowsToDelete }));
        }

        const rv = (v: PrimitiveValue): PrimitiveValue => (typeof v !== 'undefined' ? v : '');

        const answers1 = rows.map(
            (r) =>
                typeof r === 'string'
                    ? {
                          answer: [r, null, null], // multiple string
                      }
                    : { answer: [rv(r.value), rv(r.value2), rv(r.value3)], ...reverseMapRow(r) }, // images
        );

        const { answers, message, maxDiffBlockCount, blockSize, targetRepetitions, targetRepetitionsOptions } =
            await POST('add/answer', {
                question: questionId,
                answers: answers1,
            });

        const question = selectQuestionById(questionId)(getState());
        const cells = filterOnlyNewRows(question, answers.map(mapRow));

        return {
            message,
            questionId,
            cells,
            maxDiffBlockCount,
            blockSize,
            targetRepetitions,
            targetRepetitionsOptions,
        };
    },
    { notify: false },
);

export const questionReorder = createThunkRequest(
    'questions/reorder',
    async (
        {
            questionId,
            place,
            newPageId,
            overQuestionId,
        }: {
            questionId: number;
            place?: 'before' | 'after';
            newPageId?: number;
            overQuestionId: number;
        },
        { getState },
    ) => {
        const state = getState();
        const { id, isNew, pageId } = selectQuestionById(questionId)(state);

        if (isNew) return;

        const request: any = {
            id,
            page: newPageId || pageId,
        };

        if (place && overQuestionId) {
            request[place] = overQuestionId;
        }

        const { message } = await POST('change/question', request);

        return { message };
    },
    { notify: false },
);

export const saveQuestion = createThunkRequest(
    'questions/save',
    async ({ questionId, field, value }: { questionId: number; field: string; value: any }) => {
        const { message } = await POST('change/question', {
            id: questionId,
            ...reverseMapQuestion({ [field]: value }),
        });

        return { message };
    },
    { notify: false },
);

const replacePiping = (data: string | number, questions: Array<Question>) => {
    if (typeof data !== 'string') return data;

    const questionsToPiping = getMappedPipingOptions(questions);

    return data.replace(/\{Q([0-9]+)\}/gi, (_match, id) => {
        const q = questionsToPiping.find((q) => q.order === parseInt(id.toString(), 10));
        if (!q) return '';
        return `{Q-${q.value}}`;
    });
};

export const changeQuestionRow = createThunkRequest(
    'questions/changeRow',
    async (
        {
            questionId,
            rowId,
            field,
            value: newValue,
        }: {
            questionId: number;
            rowId: number;
            field: string | AnyObject<PrimitiveValue, string>;
            value?: string | number | boolean;
        },
        { getState },
    ) => {
        const questions = selectQuestions(getState());
        const q = selectQuestionById(questionId)(getState());
        const row = q.rows.find(({ id }) => id === rowId);

        // Строку могли удалить/пересоздать, пока в UI висело устаревшее событие
        // (FRONT-2AD3). Без guard'а деструктуризация undefined роняет редактор.
        if (!row) {
            return {};
        }

        const { value, value2, value3 } = row;
        let values = { value, value2, value3 };

        let req: AnyObject<PrimitiveValue, string> = {
            id: rowId,
        };

        // TODO simplify
        if (typeof field === 'string') {
            // @ts-ignore
            if (QuestionValuesKeys.has(field)) {
                values[field] = newValue;
                req.value = JSON.stringify([
                    replacePiping(values.value, questions.items),
                    replacePiping(values.value2, questions.items),
                    replacePiping(values.value3, questions.items),
                ]);
            } else {
                req = Object.assign(req, reverseMapRow({ [field]: newValue }));
            }
        } else if (typeof field === 'object') {
            req = Object.assign(req, reverseMapRow(field));
        } else if (typeof newValue === 'object') {
            values = Object.assign(values, newValue);
            req.value = JSON.stringify([values.value, values.value2, values.value3]);
        }

        const { message, maxDiffBlockCount, blockSize, targetRepetitions, targetRepetitionsOptions } = await POST(
            'change/answer',
            req,
        );

        return { message, maxDiffBlockCount, blockSize, targetRepetitions, targetRepetitionsOptions };
    },
    { notify: false },
);

export const changeQuestionCol = createThunkRequest(
    'questions/changeCol',
    async (
        {
            questionId,
            colId,
            field,
            value,
        }: {
            questionId: number;
            colId: number;
            field: string;
            value: string | number | AnyObject;
        },
        { getState },
    ) => {
        const q = selectQuestionById(questionId)(getState());
        const col = q.cols.find(({ id }) => id === colId);

        // Колонку могли удалить/пересоздать, пока в UI висело устаревшее событие.
        if (!col) {
            return {};
        }

        const { message } = await POST('change/group', {
            id: colId,
            ...reverseMapCol({ [field]: value }),
            type: col.type,
        });

        return { message };
    },
);

export const changeQuestionRows = createThunkRequest(
    'questions/changeRows',
    async (
        {
            questionId,
            data,
        }: {
            questionId: number;
            data: { [key: number]: any };
        },
        { getState },
    ) => {
        const q = selectQuestionById(questionId)(getState());
        let req = {};

        for (const [id, val] of Object.entries(data)) {
            const row = q.rows.find((r) => r.id === Number(id));

            // Устаревшее массовое изменение по уже удалённой строке — пропускаем,
            // чтобы не ронять весь запрос (тот же класс, что FRONT-2AD3).
            if (!row) {
                continue;
            }

            const { value, value2, value3 } = row;
            let valueObj: any = { value, value2, value3 };

            valueObj = Object.assign(valueObj, val);

            req[id] = {
                value: [valueObj.value, valueObj.value2, valueObj.value3],
            };
        }

        req = {
            data: JSON.stringify(req),
        };

        const { message } = await POST('change/answer', req);

        return {
            message,
            data,
            questionId,
        };
    },
    { notify: false },
);

export const addQuestionRow = createThunkRequest(
    'questions/addRow',
    async (
        {
            questionId,
            rowParams: cellParams,
        }: {
            questionId: number;
            rowParams?: AnyObject;
        },
        { getState },
    ) => {
        const { rows } = selectQuestionById(questionId)(getState());
        const prev = Array.isArray(rows) && rows.length > 0 ? rows[rows.length - 1].id : null;

        let answer: Array<any> = ['', '', ''];
        let params: any = {};

        if (cellParams) {
            answer = [cellParams.value, cellParams.value2, cellParams.value3];
            params = reverseMapRow(cellParams);
        }

        if (prev) {
            params.prev = prev;
        }

        const { answers, maxDiffBlockCount, message, blockSize, targetRepetitions, targetRepetitionsOptions } =
            await POST('add/answer', {
                question: questionId,
                answers: [
                    {
                        answer,
                        ...params,
                    },
                ],
            });
        const { id } = mapRow(answers[0]);

        return {
            message,
            questionId,
            id,
            cellParams,
            maxDiffBlockCount,
            blockSize,
            targetRepetitions,
            targetRepetitionsOptions,
        };
    },
    { notify: false },
);

export const reorderQuestionRows = createThunkRequest(
    'questions/reorderRows',
    async ({ questionId }: { questionId: number }, { getState }) => {
        const { rows } = selectQuestionById(questionId)(getState());

        const { message } = await POST('set/answer/order', {
            data: JSON.stringify(
                rows.map(({ id }: QuestionRow, index: number) => ({
                    id,
                    order: String(index + 1),
                })),
            ),
        });

        return { message };
    },
);

export const reorderQuestionCols = createThunkRequest(
    'questions/reorderCols',
    async ({ questionId }: { questionId: number }, { getState }) => {
        const { cols } = selectQuestionById(questionId)(getState());

        const { message } = await POST('set/answer_group/order', {
            data: JSON.stringify(
                cols.map(({ id }: QuestionCol, index: number) => ({
                    id,
                    order: String(index + 1),
                })),
            ),
        });

        return { message };
    },
);

export const removeQuestionCol = createThunkRequest(
    'questions/removeCol',
    async ({ questionId, colId }: { questionId: number; colId: number | Array<number> }) => {
        let message: string;
        if (!Array.isArray(colId)) {
            ({ message } = await POST('delete/group', {
                question: questionId,
                id: colId,
            }));
        } else {
            await Promise.all(
                colId.map((id) =>
                    POST('delete/group', {
                        question: questionId,
                        id,
                    }),
                ),
            );
        }

        return { message };
    },
);

export const saveQuestionParam = createThunkRequest(
    'questions/saveParam',
    async (
        {
            questionId,
            field,
            value,
            params,
        }: {
            questionId: number;
            field: string | Array<[string, PrimitiveValue]>;
            value?: PrimitiveValue;
            params?: Record<string, any>;
        },
        { getState },
    ) => {
        const { params: prevParams } = selectQuestionById(questionId)(getState());
        const newParams = getParamsData({ field, value, params });

        const { message, maxDiffBlockCount, blockSize, targetRepetitions, targetRepetitionsOptions } = await POST(
            'change/param',
            {
                object: 'question_param',
                objectid: questionId,
                name: 'params',
                value: JSON.stringify({
                    ...prevParams,
                    ...newParams,
                }),
            },
        );

        return {
            message,
            field,
            questionId,
            value,
            maxDiffBlockCount,
            blockSize,
            targetRepetitions,
            targetRepetitionsOptions,
        };
    },
    { notify: false },
);

export const moveQuestionToPage = createThunkRequest(
    'questions/moveToPage',
    async (
        {
            questionId,
            pageId,
        }: {
            questionId: number;
            pageId: number;
        },
        { dispatch, getState },
    ) => {
        const { message } = await POST('change/question', {
            id: questionId,
            page: pageId,
        });

        const surveyId = selectCurrentSurveyId(getState());

        dispatch({
            type: 'pages/selectPage',
            payload: { pageId, surveyId },
        });

        return { message };
    },
    { notify: false },
);

export const removeQuestion = createThunkRequest(
    'questions/remove',
    async (
        {
            questionId,
            pageId,
        }: {
            questionId: number;
            pageId: number;
        },
        { dispatch, getState },
    ) => {
        const state = getState();
        const { after, before } = selectNearByIds(questionId)(state);
        const { type } = selectQuestionById(questionId)(state);
        const { message } = await POST('delete/question', {
            id: questionId,
        });
        const surveyId = selectCurrentSurveyId(getState());

        dispatch(LayoutRes.Actions.setQuestionScrolling(after || before));

        await dispatch(getAllQuestions({ surveyId }));

        return {
            message,
            questionId,
            question: {
                type, // TODO purge
            },
            pageId,
        };
    },
);

export interface ColParams {
    value?: PrimitiveValue;
    name?: PrimitiveValue | Array<PrimitiveValue>;
    answers?: AnyObject;
    type?: AnswerType;
    exception?: boolean;
    order?: number;
    numericValue?: number;
}

export const addQuestionCols = createThunkRequest(
    'questions/addCols',
    async (
        {
            questionId,
            colParams,
            emptyCols,
            groups,
        }: {
            questionId: number;
            colParams: ColParams;
            emptyCols?: Array<number>;
            groups?: Array<any>;
        },
        { dispatch },
    ) => {
        if (emptyCols?.length > 0) {
            await dispatch(removeQuestionCol({ questionId, colId: emptyCols }));
        }

        let params: any = [];

        if (colParams) {
            const bulk = Array.isArray(colParams.name);

            params = bulk
                ? // @ts-ignore
                  colParams.name.map((name) =>
                      reverseMapCol({
                          ...colParams,
                          name,
                      }),
                  )
                : [reverseMapCol(colParams)];
        }

        const { groups: newGroups, message } = await POST('add/group', {
            question: questionId,
            groups: groups || params,
        });

        return {
            message,
            groups: newGroups.map(mapCol),
            questionId,
            cellParams: colParams,
        };
    },
);

export const copyQuestion = createThunkRequest(
    'questions/copy',
    async (
        {
            questionId,
            surveyId,
        }: {
            questionId: number;
            surveyId: number;
        },
        { dispatch },
    ) => {
        const { question } = await POST('copy/question', {
            id: questionId,
        });

        await dispatch(getAllQuestions({ surveyId }));

        dispatch({ type: 'questions/previewMode', payload: { questionId: question.ID } });
        dispatch(LayoutRes.Actions.setQuestionScrolling(question.ID));

        return {};
    },
);

export const removeQuestionRow = createThunkRequest(
    'questions/removeRow',
    async ({ questionId, rowId }: { questionId: number; rowId: number | Array<number> }) => {
        const { message, maxDiffBlockCount, blockSize, targetRepetitions, targetRepetitionsOptions } = await POST(
            'delete/answer',
            {
                question: questionId,
                id: rowId,
            },
        );

        return { message, maxDiffBlockCount, blockSize, targetRepetitions, targetRepetitionsOptions };
    },
    { notify: false },
);

export const addQuestion = createThunkRequest(
    'questions/add',
    async (
        {
            surveyId,
            pageId,
            afterId,
            beforeId,
            qType,
        }: {
            surveyId: number;
            pageId: number;
            afterId?: number;
            beforeId?: number;
            qType: QuestionType;
        },
        { dispatch, getState },
    ) => {
        const sibling = afterId ? { after: afterId } : beforeId ? { before: beforeId } : {};
        const { cols, name, params, rows, categories } = onAddQuestionOrRowFillers[qType];
        const { questionPresetColors } = selectSettingsState(getState());

        const newParams =
            questionPresetColors?.length > 0 ? { ...params, ...fillPresetColors(qType, questionPresetColors) } : params;

        const resp = await POST('add/question', {
            survey: surveyId,
            type: qType,
            page: pageId,
            question: name,
            params: JSON.stringify(newParams),
            answers: rows.map((r) => ({
                answer: JSON.stringify([r.value || null, r.value2 || null, r.value3 || null]),
                file_id: r.fileId || null,
                order: r.order || null,
                textBlock: r.textBlock,
            })),
            groups: cols.map(({ answers, numericValue, exception, name, order, type }: any) => ({
                type,
                name,
                order,
                exception,
                answers,
                numeric_value: numericValue,
            })),
            categories: JSON.stringify(categories),
            ...sibling,
        });

        await dispatch(getAllQuestions({ surveyId }));

        return {
            questionId: resp?.questions?.[0]?.id,
            pageId,
            questionType: qType,
        };
    },
);

export const addMultiQuestion = createThunkRequest(
    'questions/add-multi',
    async (
        {
            surveyId,
            pageId,
            questions,
            afterId,
            beforeId,
        }: {
            surveyId: number;
            pageId: number;
            questions: Array<{ title: string; answers: Array<string>; type: QuestionType }>;
            afterId?: number;
            beforeId?: number;
        },
        { dispatch },
    ) => {
        const sibling = afterId ? { after: afterId } : beforeId ? { before: beforeId } : {};
        const nextQuestions = questions.map((question) => {
            const { cols, name, params, rows } = MultiCreateFillers[question.type](question.title, question.answers);
            return {
                type: question.type,
                question: name,
                params: params,
                answers: rows.map((r) => ({
                    answer: JSON.stringify([r.value || null, r.value2 || null, r.value3 || null]),
                    file_id: r.fileId || null,
                    order: r.order || null,
                })),
                groups: cols.map(({ answers, numericValue, exception, name, order, type }: any) => ({
                    type,
                    name,
                    order,
                    exception,
                    answers,
                    numeric_value: numericValue,
                })),
            };
        });

        await POST('add/question', {
            questions: nextQuestions,
            survey: surveyId,
            page: pageId,
            ...sibling,
        } as any);

        await dispatch(getAllQuestions({ surveyId }));

        return {};
    },
);

export const getQuestionsWithPages = createThunkRequest(
    'questions/getQuestionsWithPages',
    async ({ surveyId }: { surveyId: number }) => {
        const { pages, questions } = await POST('list/page_question', {
            survey: surveyId,
        });
        let questionsMapped = reorderQuestions(questions, pages).map(mapQuestion);
        questionsMapped = questionsMapped.map((question) => deepMapQuestion(question, questionsMapped));

        return {
            pages: pages.map(mapPage),
            questions: questionsMapped,
        };
    },
    { notify: false },
);

export const copyQuestionsFrom = createThunkRequest(
    'questions/copyQuestionsFrom',
    async (
        {
            pageId,
            targetSurveyId,
            questionIds,
            afterQuestionId,
            beforeQuestionId,
        }: {
            pageId: number;
            targetSurveyId: number;
            questionIds: Array<number>;
            afterQuestionId?: number;
            beforeQuestionId?: number;
        },
        { dispatch },
    ) => {
        await POST('copy/questions_from', {
            targetSurveyId,
            questionIds,
            pageId,
            afterQuestionId,
            beforeQuestionId,
        });

        await dispatch(getAllQuestions({ surveyId: targetSurveyId }));

        return {};
    },
    { notify: false },
);

export const getAllQuestions = createThunkRequest(
    'questions/getAll',
    async ({ pageId, surveyId }: { pageId: number; surveyId: number }) => {
        const { pages, questions } = await POST('list/page_question', {
            survey: surveyId,
        });

        let questionsMapped: Array<Question> = reorderQuestions(questions, pages).map((item) =>
            mapQuestion(item, false),
        );
        questionsMapped = questionsMapped.map((question) => deepMapQuestion(question, questionsMapped));

        return {
            questions: questionsMapped,
            pageId: typeof pageId === 'undefined' ? null : pageId,
        };
    },
    { notify: false },
);

export const changeQuestionType = createThunkRequest(
    'questions/changeType',
    async ({ questionId, type }: { questionId: number; type: number }) => {
        const { message, question } = await POST('change/question_type', {
            question_id: questionId,
            question_type: type,
        });

        return {
            questionId,
            question: mapQuestion(question),
            message,
        };
    },
    { notify: false },
);

export const nestedAdd = createThunkRequest(
    'nested/add',
    async ({
        answerId,
        items,
        level,
        parentId,
        questionId,
    }: {
        answerId: number;
        items: any;
        level: number;
        parentId: number;
        questionId: number;
    }) => {
        const req = {
            question_id: questionId,
            answer_id: answerId,
            nested_items: [],
            level,
        };

        for (const i of items) {
            req.nested_items.push({
                answer_id: answerId,
                parent_id: parentId,
                level,
                name: i,
            });
        }

        const { id, message } = await POST('add/nested', req);

        return {
            message,
            questionId,
            answerId,
            level,
            id,
        };
    },
);

export const nestedChange = createThunkRequest(
    'nested/change',
    async ({ itemId, name, questionId }: { itemId: number; name: string; questionId: number }) => {
        const { message } = await POST('change/nested_item', {
            item_id: itemId,
            question_id: questionId,
            name,
        });

        return {
            questionId,
            message,
            name,
            itemId,
        };
    },
);

export const nestedDelete = createThunkRequest(
    'nested/delete',
    async ({ itemId, questionId, type }: { itemId: number; questionId: number; type: number }) => {
        const { message } = await POST('delete/nested_item', {
            item_id: itemId,
        });

        return {
            questionId,
            itemId,
            message,
            type,
        };
    },
);

export const getNested = createThunkRequest(
    'nested/show',
    async ({ questionId, parentId, level }: { questionId: number; parentId: number; level: number }) => {
        const req: any = {
            question_id: questionId,
            level,
        };

        if (parentId) {
            req.parent_id = parentId;
        }

        const { items } = await POST('show/nested_items', req);

        return {
            items: items.map(mapNested),
        };
    },
    { notify: false },
);

export const addQuestionCategories = createThunkRequest(
    'categories/add',
    async ({ question, categories }: { question: number; categories: Array<any> }) => {
        const resp = await POST('add/category', { question, categories });

        return {
            questionId: question,
            categories: resp.categories.map(mapCategory),
        };
    },
);

export const changeQuestionCategories = createThunkRequest(
    'categories/change',
    async ({ question, data }: { question: number; data: Record<string, any> }) => {
        const resp = await POST('change/category', { question, data });

        return {
            questionId: question,
            categories: resp.categories,
        };
    },
);

export const removeQuestionCategories = createThunkRequest(
    'categories/remove',
    async ({ questionId, id }: { questionId: number; id: Array<number> }) => {
        await POST('delete/category', { id });

        return {
            questionId,
            id,
        };
    },
);

export const reorderQuestionCategories = createThunkRequest(
    'categories/sort',
    async ({ questionId }: { questionId: number }, { getState }) => {
        const { categories } = selectQuestionById(questionId)(getState());

        const { message } = await POST('set/category/order', {
            data: JSON.stringify(
                categories.map(({ id }: IQuestionCategory, index: number) => ({
                    id,
                    order: String(index + 1),
                })),
            ),
        });

        return { message };
    },
);

export const saveConjointConfig = createThunkRequest(
    'conjoint_config/save',
    async ({
        questionId,
        attributes,
        tasks,
    }: {
        questionId: number;
        attributes: Record<string, any>;
        tasks: Record<string, any>;
    }) => {
        const resp = await POST('save/conjoint_config', { questionId, attributes, tasks }, { json: true });

        return resp.config;
    },
    { notify: false },
);

export const generateConjointConfig = createThunkRequest(
    'conjoint_config/generate',
    async ({ questionId, screenCount }: { questionId: number; screenCount: number }) => {
        return await POST('save/conjoint_generate', { questionId, screenCount });
    },
);

export const getConjointConfig = createThunkRequest(
    'conjoint_config/get',
    async ({ questionId }: { questionId: number }) => {
        const resp = await POST('list/conjoint_config', { questionId });

        return resp.config;
    },
);
